import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { storage } from '../utils/storage';
import { authApi } from '../services/api';
import { reconnectSocket } from '../services/socket';
import { setupNotifications } from '../services/firebase';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (phone: string, password: string) => Promise<void>;
  register: (phone: string, firstName: string, lastName: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from storage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = storage.getUser<User>();
        const token = storage.getAccessToken();

        if (storedUser && token) {
          // Verify token is still valid
          const { user: freshUser } = await authApi.getMe();
          setUser(freshUser);
          storage.setUser(freshUser);
        }
      } catch (error) {
        console.error('Auth init error:', error);
        storage.clearTokens();
        storage.clearUser();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (phone: string, password: string) => {
    const { user: userData, accessToken, refreshToken } = await authApi.login(phone, password);

    storage.setAccessToken(accessToken);
    storage.setRefreshToken(refreshToken);
    storage.setUser(userData);

    setUser(userData);

    // Reconnect socket with new token
    reconnectSocket();

    // Setup notifications (non-blocking - don't wait)
    setupNotifications().catch(console.error);
  }, []);

  const register = useCallback(async (phone: string, firstName: string, lastName: string, password: string) => {
    const { user: userData, accessToken, refreshToken } = await authApi.register(phone, firstName, lastName, password);

    storage.setAccessToken(accessToken);
    storage.setRefreshToken(refreshToken);
    storage.setUser(userData);

    setUser(userData);

    // Reconnect socket with new token
    reconnectSocket();

    // Setup notifications (non-blocking - don't wait)
    setupNotifications().catch(console.error);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      storage.clearTokens();
      storage.clearUser();
      setUser(null);
    }
  }, []);

  const updateUser = useCallback(async (updates: Partial<User>) => {
    const { user: updatedUser } = await authApi.updateMe(updates);
    setUser(updatedUser);
    storage.setUser(updatedUser);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user: freshUser } = await authApi.getMe();
      setUser(freshUser);
      storage.setUser(freshUser);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
  }), [user, isLoading, login, register, logout, updateUser, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
